import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import {
  kpiTrendsFilterLogConfig,
  dbiBtnLogMes,
} from '@intelligence/utils/buriedPointCfg';
import {
  MY_FILTER,
  RANGE_TIME,
  METRIC_SELECT_FOR_KPI_TREND,
  METRICTABLE_DEVICE,
  CASCADER,
  REGION,
  DIGITAL_SEGMENT_MULTIPLE,
  PLATFORMCASCADER,
  PUBLISHER,
  METRIC_SUBKEY_METRIC_TYPE,
  METRIC_SUBKEY_SHOT,
  METRIC_SUBKEY_SHOT_METRIC,
  METRIC_SUBKEY_SHOT_DATA_SOURCE,
} from '@intelligence/components/Filter/FilterBase/_config';

interface ICascaderOption {
  value: string;
  children?: ICascaderOption[] | null;
}

interface FilterConfig {
  pageId: string;
  buttonId: string;
  typeId: string;
  filterKey: string;
  valueExtractor: (prev: any, curr: any) => any;
}

/**
 * Mapping from filterKey to component display names for logging
 */
const FILTER_COMPONENT_NAMES: Record<string, string> = {
  [MY_FILTER]: 'My Filter',
  [RANGE_TIME]: 'Date',
  [PUBLISHER]: 'GSD Filter',
  [METRICTABLE_DEVICE]: 'Metric Type',
  [CASCADER]: 'Segment',
  [PLATFORMCASCADER]: 'Platform',
  [REGION]: 'Region',
  [DIGITAL_SEGMENT_MULTIPLE]: 'Segment',
  [METRIC_SELECT_FOR_KPI_TREND]: 'Metrics',
  [METRIC_SUBKEY_SHOT]: 'Shot',
  [METRIC_SUBKEY_SHOT_METRIC]: 'Metric Type',
  [METRIC_SUBKEY_SHOT_DATA_SOURCE]: 'Data Source',
  [METRIC_SUBKEY_METRIC_TYPE]: 'Metric Types/Source Button',
  visualMetricSelectorDual: 'Visualization Metrics Selector',
};

/**
 * Simple utility function for logging filter changes in KPI Trends
 * @param filterKey - The filter key that changed
 * @param filterValue - The new filter value
 * @param prevValue - The previous filter value
 * @param gameId - Game ID for logging context
 * @param gameName - Game name for logging context
 */
export function logKpiTrendsFilterChange(
  filterKey: string,
  filterValue: any,
  prevValue: any,
  gameId: string,
  gameName: string,
): void {
  // Find the configuration that matches this filter key and has the required properties
  let config: FilterConfig | undefined;

  // Special routing for KPI Trends metric selection to subkey-specific configs
  if (filterKey !== METRIC_SELECT_FOR_KPI_TREND) {
    config = Object.values(kpiTrendsFilterLogConfig).find(
      (config): config is FilterConfig => {
        return (
          config &&
          typeof config === 'object' &&
          'filterKey' in config &&
          'valueExtractor' in config &&
          config.filterKey === filterKey
        );
      },
    );
  }

  if (filterKey === METRIC_SELECT_FOR_KPI_TREND) {
    const subKey = filterValue?.value?.subKey || filterValue?.subKey;
    const selectedMetricsValue =
      filterValue?.value?.selectedMetricsValue ||
      filterValue?.selectedMetricsValue;

    if (subKey === METRIC_SUBKEY_METRIC_TYPE) {
      // Toggle between metrics/data source tabs
      // @ts-ignore known shape in config map
      config = (kpiTrendsFilterLogConfig as any)
        .metricSelectForKpiTrendMetricType;
    } else if (subKey === METRIC_SUBKEY_SHOT) {
      if (selectedMetricsValue === 'metrics') {
        // @ts-ignore known shape in config map
        config = (kpiTrendsFilterLogConfig as any)
          .metricSelectForKpiTrendShotMetric;
      } else if (selectedMetricsValue === 'dataSource') {
        // @ts-ignore known shape in config map
        config = (kpiTrendsFilterLogConfig as any)
          .metricSelectForKpiTrendShotDataSource;
      }
    } else {
      // Fallback to base config to compare metrics/sources arrays
      // @ts-ignore known shape in config map
      config = (kpiTrendsFilterLogConfig as any).metricSelectForKpiTrend;
    }
  }

  if (!config) {
    console.log(
      'KpiTrendsLogger: No configuration found for filter key:',
      filterKey,
    );
    return;
  }

  // Use the value extractor to determine if there's a meaningful change and get the component value
  const componentValue = config.valueExtractor(prevValue, filterValue);

  if (componentValue) {
    const isHourTabClick =
      filterKey === RANGE_TIME &&
      prevValue?.type !== 'hour' &&
      filterValue?.type === 'hour';
    const buttonId = isHourTabClick
      ? dbiBtnLogMes.kpiTrendsHourTabClick.buttonId
      : config.buttonId;

    // Create the buried point configuration using the centralized config
    const buriedPointConfig = {
      pageId: config.pageId,
      buttonId,
      typeId: config.typeId,
      extInfo: JSON.stringify({
        gameId,
        gameName,
        componentName:
          FILTER_COMPONENT_NAMES[config.filterKey] || config.filterKey,
        componentValue: componentValue,
      }),
    };

    console.log(
      'KpiTrendsLogger: Sending buried point log:',
      buriedPointConfig,
    );

    // Send the log
    reportButtonLog(buriedPointConfig);
  } else {
    console.log(
      'KpiTrendsLogger: No meaningful change detected for filter:',
      filterKey,
    );
  }
}

/**
 * Simple utility function for logging VisualMetricSelectorDual changes in KPI Trends
 * @param prevValues - The previous selected metric values
 * @param newValues - The new selected metric values
 * @param gameId - Game ID for logging context
 * @param gameName - Game name for logging context
 */
export function logVisualMetricSelectorDualChange(
  prevValues: string[],
  newValues: string[],
  gameId: string,
  gameName: string,
): void {
  const config = dbiBtnLogMes.visualMetricSelectorDual;

  const buriedPointConfig = {
    pageId: config.pageId,
    buttonId: config.buttonId,
    typeId: config.typeId,
    extInfo: JSON.stringify({
      gameId,
      gameName,
      componentName: config.componentName,
      componentValue: newValues,
    }),
  };

  console.log(
    'VisualMetricSelectorDualLogger: Sending buried point log:',
    buriedPointConfig,
  );

  // Send the log
  reportButtonLog(buriedPointConfig);
}

/**
 * Simple utility function for logging calibration data clicks in KPI Trends
 * @param metric - The metric that was clicked for calibration
 * @param gameId - Game ID for logging context
 * @param gameName - Game name for logging context
 */
export function logCalibrationClick(
  metric: string,
  gameId: string,
  gameName: string,
): void {
  const config = dbiBtnLogMes.metricHoverExplinationClick;

  const buriedPointConfig = {
    pageId: config.pageId,
    buttonId: config.buttonId,
    typeId: config.typeId,
    extInfo: JSON.stringify({
      gameId,
      gameName,
      componentName: config.componentName,
      componentValue: {
        clickedMetric: metric,
        action: 'view_calibration_data',
      },
    }),
  };

  // Send the log
  reportButtonLog(buriedPointConfig);
}
